import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, CircularProgress, Alert,
  RadioGroup, FormControlLabel, Radio, FormLabel, FormControl
} from '@mui/material';
import { EmojiEvents } from '@mui/icons-material';
import verifierService from '../../services/verifierService';
import DialogCloseButton from '../common/DialogCloseButton';

const BRAND_GREEN = '#43B581';
const BRAND_DARK = '#27ae60';

/**
 * TierUpgradeDialog
 * Allows a verifier to submit a request to be upgraded from General → Professional tier.
 */
const TierUpgradeDialog = ({ open, onClose, currentTier }) => {
  const [reason, setReason] = useState('');
  const [yearsExperience, setYearsExperience] = useState('');
  const [certCount, setCertCount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for your upgrade request.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      // Store upgrade request as part of profile — backend can review this field
      await verifierService.updateMyProfile({
        tierUpgradeRequested: true,
        tierUpgradeReason: reason.trim(),
        yearsExperience: yearsExperience ? parseInt(yearsExperience) : undefined
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit upgrade request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setYearsExperience('');
    setCertCount('');
    setError('');
    setSubmitted(false);
    onClose();
  };

  if (currentTier === 'professional') {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={handleClose} />
        <DialogTitle>Already a Professional Verifier</DialogTitle>
        <DialogContent>
          <Alert severity="success">
            You are already a Professional Verifier. No upgrade needed.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogCloseButton onClick={handleClose} />
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmojiEvents sx={{ color: '#f39c12' }} />
          Request Professional Tier Upgrade
        </Box>
      </DialogTitle>
      <DialogContent>
        {submitted ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            Your upgrade request has been submitted. Our team will review it and contact you within 2–3 business days.
          </Alert>
        ) : (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Professional Verifiers get priority job assignments, higher pay rates, and a verified badge on their public profile.
              Tell us why you qualify for a Professional tier upgrade.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TextField
              fullWidth
              label="Years of Verification Experience"
              type="number"
              value={yearsExperience}
              onChange={(e) => setYearsExperience(e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Number of Certifications Held"
              type="number"
              value={certCount}
              onChange={(e) => setCertCount(e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Why do you qualify for a Professional upgrade?"
              placeholder="Describe your experience, notable projects, certifications, and specializations that make you eligible for Professional status..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {submitted ? 'Close' : 'Cancel'}
        </Button>
        {!submitted && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ bgcolor: BRAND_GREEN, '&:hover': { bgcolor: BRAND_DARK } }}
          >
            Submit Request
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TierUpgradeDialog;
